import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Icon } from '@iconify/react';
import { Combobox } from 'medere-ui-framework';
import FileAttachmentPanel from './FileAttachmentPanel';
import {
  getIndicationTypes,
  getIndicationTemplate,
  getAntecedentsText,
  getSenderPermissions,
  saveIndication,
  getErrorMessage,
} from '../../../services/MedicalIndicationController';
import type {
  IndicationType,
  UploadedFile,
  SenderPermissions,
  SaveIndicationResponse,
} from '../../../services/MedicalIndicationController';

import type { Professional } from '../../../interface/interface';
import './MedicalIndicationForm.scss';
import { getProfessionalData, isProfessionalUser, loadProfessionalsPermissionsForRpGivenToUser } from '../../../servicesHooks/DigitalRP/useDigitalRPService';

interface MedicalIndicationFormProps {
  patientId: number;
  embeddedProfessionalId?: number | null;
  onSaved: (result: SaveIndicationResponse) => void;
  onSwitchToStudyOrder?: () => void;
  onCancel?: () => void;
}

const SENDER_OPTIONS = [
  { label: 'Email', value: 'email' as const },
  { label: 'WhatsApp', value: 'whatsapp' as const },
];

export default function MedicalIndicationForm({
  patientId,
  embeddedProfessionalId,
  onSaved,
  onSwitchToStudyOrder,
  onCancel,
}: MedicalIndicationFormProps) {
  const toast = useRef<Toast>(null);

  // ── Loading state ──
  const [loadingInit, setLoadingInit] = useState(true);

  // ── Professional ──
  const [isProf, setIsProf] = useState<boolean | null>(null);
  const [professional, setProfessional] = useState<Professional | null>(null);
  const [selectedProfessional, setSelectedProfessional] = useState<any>(null);

  // ── Indication types ──
  const [indicationTypes, setIndicationTypes] = useState<IndicationType[]>([]);
  const [selectedType, setSelectedType] = useState<IndicationType | null>(null);

  // ── Text ──
  const [indicationText, setIndicationText] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // ── Files ──
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // ── Sender ──
  const [senderPerms, setSenderPerms] = useState<SenderPermissions>({ email: false, whatsapp: false });
  const [sendVia, setSendVia] = useState<'email' | 'whatsapp' | null>(null);
  const [recipient, setRecipient] = useState('');

  // ── Options ──
  const [printOnSave, setPrintOnSave] = useState(true);
  const [saving, setSaving] = useState(false);

  // ── Init: load professional, types, sender permissions ──
  useEffect(() => {
    let active = true;

    Promise.all([
      isProfessionalUser(),
      getIndicationTypes(),
      getSenderPermissions(),
    ]).then(([profResult, types, perms]) => {
      if (!active) return;
      setIsProf(profResult);
      setIndicationTypes(types);
      setSenderPerms(perms);

      if (profResult) {
        getProfessionalData().then((prof) => {
          if (active) setProfessional(prof);
        });
      }
    }).finally(() => {
      if (active) setLoadingInit(false);
    });

    return () => { active = false; };
  }, []);

  // ── Available sender options based on permissions ──
  const availableSenderOptions = useMemo(
    () => SENDER_OPTIONS.filter((o) =>
      (o.value === 'email' && senderPerms.email) ||
      (o.value === 'whatsapp' && senderPerms.whatsapp),
    ),
    [senderPerms],
  );

  // ── Resolved professional ID ──
  const resolvedProfessionalId = useMemo(() => {
    if (isProf && professional) return professional.professionaid;
    if (selectedProfessional) return selectedProfessional.professionaid;
    if (embeddedProfessionalId) return embeddedProfessionalId;
    return null;
  }, [isProf, professional, selectedProfessional, embeddedProfessionalId]);

  // ── Type change handler ──
  const handleTypeChange = useCallback(
    async (e: DropdownChangeEvent) => {
      const type = e.value as IndicationType | null;
      setSelectedType(type);

      if (!type) {
        setIndicationText('');
        return;
      }

      if (type.redirectToStudyOrder) {
        setSelectedType(null);
        onSwitchToStudyOrder?.();
        return;
      }

      setLoadingTemplate(true);
      try {
        const text = await getIndicationTemplate(type.id, patientId);
        setIndicationText(text);
      } catch (error) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: getErrorMessage(error, 'No se pudo cargar el template'),
          life: 4000,
        });
      } finally {
        setLoadingTemplate(false);
      }
    },
    [patientId, onSwitchToStudyOrder],
  );

  // ── Add antecedents ──
  const handleAddAntecedents = useCallback(async () => {
    try {
      const text = await getAntecedentsText(patientId);
      if (!text.trim()) {
        toast.current?.show({
          severity: 'info',
          summary: 'Sin antecedentes',
          detail: 'No se encontraron antecedentes registrados para el paciente',
          life: 3000,
        });
        return;
      }
      setIndicationText((prev) => prev + '\n\nAntecedentes:\n' + text);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: getErrorMessage(error, 'No se pudieron cargar los antecedentes'),
        life: 4000,
      });
    }
  }, [patientId]);

  // ── Save ──
  const handleSave = useCallback(async () => {
    if (!resolvedProfessionalId) {
      toast.current?.show({ severity: 'warn', summary: 'Campo requerido', detail: 'Seleccione un profesional', life: 3000 });
      return;
    }
    if (!selectedType) {
      toast.current?.show({ severity: 'warn', summary: 'Campo requerido', detail: 'Seleccione un tipo de indicación', life: 3000 });
      return;
    }
    if (!indicationText.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Campo requerido', detail: 'El texto de la indicación no puede estar vacío', life: 3000 });
      return;
    }

    setSaving(true);
    try {
      const result = await saveIndication({
        patientId,
        professionalId: resolvedProfessionalId,
        indicationTypeId: selectedType.id,
        indicationName: selectedType.name,
        text: indicationText,
        print: printOnSave,
        tempFileIds: uploadedFiles.map((f) => f.tempFileId),
        sendVia: sendVia,
        recipient: sendVia ? recipient : null,
      });

      if (printOnSave && result.encodedPdf) {
        const blob = new Blob(
          [Uint8Array.from(atob(result.encodedPdf), (c) => c.charCodeAt(0))],
          { type: 'application/pdf' },
        );
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }

      onSaved(result);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error al guardar',
        detail: getErrorMessage(error, 'No se pudo guardar la indicación médica'),
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  }, [resolvedProfessionalId, selectedType, indicationText, patientId, printOnSave, uploadedFiles, sendVia, recipient, onSaved]);

  // ── Loading ──
  if (loadingInit) {
    return (
      <div className="mi-form-loading">
        <ProgressSpinner style={{ width: '40px', height: '40px' }} />
        <span>Cargando...</span>
      </div>
    );
  }

  return (
    <div className="mi-form">
      <Toast ref={toast} />

      {/* Professional selector (only for secretaries) */}
      {isProf === false ? (
        <div className="mi-form__field">
          <label className="mi-form__label">Profesional que prescribe *</label>
          <Combobox
            value={selectedProfessional}
            setValue={setSelectedProfessional}
            getItems={(props: any) =>
              loadProfessionalsPermissionsForRpGivenToUser(props).then((res: any) => res ?? [])
            }
            optionLabel="fulldescription"
            placeholder="Buscar profesional..."
            className="border2"
            style={{ width: '100%' }}
          />
        </div>
      ) : null}

      {/* Indication type */}
      <div className="mi-form__field">
        <label className="mi-form__label">Tipo de indicación *</label>
        <Dropdown
          value={selectedType}
          options={indicationTypes}
          optionLabel="name"
          onChange={handleTypeChange}
          placeholder="Seleccione un tipo de indicación"
          className="mi-form__dropdown"
          showClear
        />
      </div>

      {/* Add antecedents button */}
      <div className="mi-form__actions-row">
        <Button
          className="p-button-outlined p-button-sm mi-form__antecedents-btn"
          onClick={handleAddAntecedents}
          disabled={!selectedType}
        >
          <Icon icon="mdi:clipboard-text-outline" style={{ fontSize: '0.9rem' }} />
          <span>Agregar antecedentes</span>
        </Button>
      </div>

      {/* Text area */}
      <div className="mi-form__field">
        {loadingTemplate ? (
          <div className="mi-form__textarea-loading">
            <ProgressSpinner style={{ width: '24px', height: '24px' }} />
            <span>Cargando template...</span>
          </div>
        ) : (
          <InputTextarea
            value={indicationText}
            onChange={(e) => setIndicationText(e.target.value)}
            rows={10}
            autoResize
            disabled={!selectedType}
            placeholder={selectedType ? 'Escribir indicación...' : 'Seleccione un tipo de indicación para comenzar'}
            className="mi-form__textarea"
          />
        )}
      </div>

      {/* File attachments */}
      <FileAttachmentPanel
        files={uploadedFiles}
        onFilesChange={setUploadedFiles}
        disabled={!selectedType}
      />

      {/* Sender section */}
      {availableSenderOptions.length > 0 ? (
        <div className="mi-form__sender-row">
          <div className="mi-form__sender-field">
            <label className="mi-form__label">Enviar vía</label>
            <Dropdown
              value={sendVia}
              options={availableSenderOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setSendVia(e.value)}
              placeholder="No enviar"
              showClear
              className="mi-form__dropdown mi-form__dropdown--sm"
            />
          </div>
          {sendVia ? (
            <div className="mi-form__sender-field mi-form__sender-field--grow">
              <label className="mi-form__label">
                {sendVia === 'email' ? 'Email' : 'WhatsApp (+cód. país cód. área número)'}
              </label>
              <input
                type={sendVia === 'email' ? 'email' : 'tel'}
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder={sendVia === 'email' ? 'correo@ejemplo.com' : '+542236236647'}
                className="p-inputtext mi-form__input"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Footer: print + save */}
      <div className="mi-form__footer">
        <div className="mi-form__footer-left">
          <Checkbox
            inputId="mi-print"
            checked={printOnSave}
            onChange={(e: CheckboxChangeEvent) => setPrintOnSave(e.checked ?? true)}
          />
          <label htmlFor="mi-print" className="mi-form__label mi-form__label--inline">
            Imprimir al guardar
          </label>
        </div>
        <div className="mi-form__footer-right">
          {onCancel ? (
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="p-button-outlined mi-form__cancel-btn"
              onClick={onCancel}
              disabled={saving}
            />
          ) : null}
          <Button
            label="Guardar indicación"
            icon="pi pi-check"
            className="mi-form__save-btn"
            onClick={handleSave}
            loading={saving}
            disabled={!selectedType || !indicationText.trim() || !resolvedProfessionalId}
          />
        </div>
      </div>
    </div>
  );
}
